import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'components/priority/Icon/Icon'
import {
  editorTextBoxColorName,
  type EditorTextBoxSwatch,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import * as S from './EditorTextBoxToolbar.styled'

export type ColorTileGridProps = {
  swatches: readonly EditorTextBoxSwatch[]
  onPick: (hex: string) => void
  onRemove: () => void
  onCustom: () => void
  /** `color`, `outline`…: names the grid's own data attributes. */
  attrPrefix: string
  /** Kept mounted but out of layout while another view of the popover shows. */
  hidden?: boolean
}

/**
 * TinyMCE's colour picker as tiles: the palette, then a "remove" tile (red
 * slash) and a "custom" tile (palette glyph). Every tile keeps focus in the
 * editing host by cancelling its mousedown; what a pick, a remove or the
 * custom tile mean is the caller's.
 */
const ColorTileGrid: FC<ColorTileGridProps> = ({
  swatches,
  onPick,
  onRemove,
  onCustom,
  attrPrefix,
  hidden,
}) => {
  const { t } = useTranslation()
  return (
    <S.TileGrid
      hidden={hidden}
      {...{ [`data-editor-text-${attrPrefix}-tiles`]: true }}
    >
      {swatches.map((swatch) => (
        <S.Tile
          key={`${swatch.name}:${swatch.hex}`}
          type="button"
          title={editorTextBoxColorName(t, swatch.name)}
          data-editor-text-swatch={swatch.hex}
          {...{ [`data-editor-text-${attrPrefix}-swatch`]: swatch.hex }}
          style={{ background: swatch.hex }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(swatch.hex)}
        />
      ))}
      <S.Tile
        type="button"
        $glyph
        title={t('editor.textBox.toolbar.removeColor')}
        {...{ [`data-editor-text-${attrPrefix}-remove`]: true }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onRemove}
      >
        <Icon icon="colorRemoveIcon" width="24px" height="24px" />
      </S.Tile>
      <S.Tile
        type="button"
        $glyph
        title={t('editor.textBox.toolbar.customColor')}
        {...{ [`data-editor-text-${attrPrefix}-custom`]: true }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCustom}
      >
        <Icon icon="colorPaletteIcon" width="24px" height="24px" />
      </S.Tile>
    </S.TileGrid>
  )
}

export default ColorTileGrid
